// the validation rules for the sign-in / create-account forms, written as a
// small class hierarchy.
//
// every field is checked in the same order - type, existence, range, format -
// and the first thing that fails wins. that order is the same for all three
// fields, so it lives once in the superclass (Validator.validate below) and the
// subclasses only fill in the rules that are actually different. adding a fourth
// field later means writing its rules, not re-writing the order they run in.
//
// a check returns '' when the value is fine, or the message to show if not.

// how long things are allowed to be. pulled out here so the numbers aren't
// buried in three different places, and so the forgot-password page can reuse
// the same rules rather than growing its own slightly different copy.
export const LIMITS = {
  nameMin: 2,
  nameMax: 60,
  emailMax: 254,      // the longest an email address can legally be
  emailLocalMax: 64,  // ...and the longest the bit before the @ can be
  passwordMin: 8,
  passwordMax: 72     // bcrypt's hard limit - a longer password was never accepted at sign-up either
};

// letters (any language), spaces, hyphens, apostrophes and full stops
const NAME_PATTERN = /^[\p{L}\p{M}'\-. ]+$/u;
// deliberately not the "perfect" rfc email regex - that thing is unreadable and
// still can't tell you if an address actually exists. this catches typos.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// the superclass. it doesn't know a single rule about names or emails - all it
// knows is the order the checks run in and how to say the two things every
// field says the same way ("that isn't a string" and "you left it blank").
export class Validator {
  // fieldName goes in the type message ("the email field"), label goes in the
  // blank message ("please enter your email address"). they're different words
  // for the same field, which is why both get passed in.
  constructor(fieldName, label) {
    this.fieldName = fieldName;
    this.label = label;
  }

  // the one every subclass is called through. each step gets the chance to
  // return a message, and the moment one does we stop and hand it back - so the
  // user is told about the first problem rather than all five at once.
  validate(value) {
    // type comes first and gets the raw value, because everything below here
    // calls string methods on it
    const typeError = this.checkType(value);
    if (typeError) return typeError;

    // tidy it up once, then check the tidied version, so a name typed as
    // "  Ana  " isn't judged on spaces the user can't even see
    const cleaned = this.clean(value);

    return (
      this.checkExistence(cleaned) ||
      this.checkRange(cleaned) ||
      this.checkFormat(cleaned) ||
      this.checkParts(cleaned) ||
      ''
    );
  }

  // most fields have surrounding whitespace that nobody meant to type, so
  // trimming is the sensible default. PasswordValidator overrides this.
  clean(value) {
    return value.trim();
  }

  // state should always hold a string, but if something odd got in there we'd
  // rather say so than call .trim() on it and crash the page. identical for
  // every field apart from the field's name, so no subclass overrides it.
  checkType(value) {
    if (typeof value !== 'string') {
      return `Something went wrong with the ${this.fieldName} field - please retype it.`;
    }
    return '';
  }

  // also the same everywhere once you swap the label in
  checkExistence(value) {
    if (value === '') return `Please enter your ${this.label}.`;
    return '';
  }

  // the rest are the per-field bits, so the superclass has nothing useful to
  // say about them. they return '' - "nothing wrong here" - and a subclass
  // overrides the ones it actually has rules for. that way a subclass with no
  // format rule simply doesn't mention format, instead of having to write out
  // an empty method to satisfy the base class.

  // is it too long / too short?
  checkRange(value) {
    return '';
  }

  // is it shaped the way this kind of value is shaped?
  checkFormat(value) {
    return '';
  }

  // checks on a piece of the value that only make sense once the format has
  // passed - measuring the bit before the @ is meaningless until we know there
  // is an @. that's the whole reason this step exists after checkFormat rather
  // than being folded into checkRange.
  checkParts(value) {
    return '';
  }
}

export class NameValidator extends Validator {
  constructor() {
    super('name', 'name');
  }

  checkRange(name) {
    if (name.length < LIMITS.nameMin) return `That name is too short - please use at least ${LIMITS.nameMin} characters.`;
    if (name.length > LIMITS.nameMax) return `That name is too long - please keep it to ${LIMITS.nameMax} characters or fewer.`;
    return '';
  }

  // this message has to list exactly what NAME_PATTERN allows, full stops
  // included, or someone called "St. John" gets told their name is invalid
  checkFormat(name) {
    if (!NAME_PATTERN.test(name)) return 'Names can only contain letters, spaces, hyphens, apostrophes and full stops.';
    return '';
  }
}

export class EmailValidator extends Validator {
  constructor() {
    super('email', 'email address');
  }

  checkRange(email) {
    if (email.length > LIMITS.emailMax) return `That email address is too long - the maximum is ${LIMITS.emailMax} characters.`;
    return '';
  }

  checkFormat(email) {
    if (!EMAIL_PATTERN.test(email)) return "That doesn't look like a valid email address - check for typos like a missing @ or .com";
    return '';
  }

  // safe to split on '@' here - checkFormat has already run and confirmed
  // there's exactly one of them with something on either side
  checkParts(email) {
    if (email.split('@')[0].length > LIMITS.emailLocalMax) {
      return `The part before the @ is too long - the maximum is ${LIMITS.emailLocalMax} characters.`;
    }
    return '';
  }
}

// the password rules that apply on both tabs. on the sign-in tab this is the
// whole policy: an older account might have a password that wouldn't pass
// today's rules, and blocking it here would lock someone out of their own
// account for no good reason. the max length is the one limit that's still safe
// to enforce, since it was in force when they signed up too, so no working
// password can be longer than it.
export class PasswordValidator extends Validator {
  constructor() {
    super('password', 'password');
  }

  // the only field we don't trim. a password's spaces are part of it, and we
  // want checkFormat below to be able to complain about the ones on the ends
  // rather than quietly removing them and accepting a different password than
  // the one that was typed.
  clean(value) {
    return value;
  }

  checkRange(password) {
    if (password.length > LIMITS.passwordMax) return `That password is too long - please keep it to ${LIMITS.passwordMax} characters or fewer.`;
    return '';
  }
}

// the stricter policy for a brand new password. it's a subclass rather than an
// `if (signing up)` inside PasswordValidator because it only ever *adds* to what
// its parent already does - which is exactly what the code below says.
export class SignUpPasswordValidator extends PasswordValidator {
  checkRange(password) {
    // run the parent's length check first so the "too long" message still wins,
    // then add the minimum that only applies to a password being set now
    const tooLong = super.checkRange(password);
    if (tooLong) return tooLong;

    if (password.length < LIMITS.passwordMin) return `Your password needs to be at least ${LIMITS.passwordMin} characters.`;
    return '';
  }

  // the parent has no format rules at all, so there's nothing to call super for
  checkFormat(password) {
    if (password !== password.trim()) return 'Your password can’t start or end with a space.';
    if (!/[a-zA-Z]/.test(password)) return 'Your password needs at least one letter.';
    if (!/[0-9]/.test(password)) return 'Your password needs at least one number.';
    return '';
  }
}

// the validators hold no state between calls, so one of each is plenty - no
// point building a new object on every keystroke
export const nameValidator = new NameValidator();
export const emailValidator = new EmailValidator();

// which password policy applies depends on the tab, and this is the only place
// that decides it. the pages just say which tab they're on.
export function passwordValidatorFor(tab) {
  return tab === 'signup' ? new SignUpPasswordValidator() : new PasswordValidator();
}
